import { Brain, Lightbulb, Sparkles } from "lucide-react";
import * as S from "../dashboard/_shared";
import Section from "../section";
import Tag from "../tag";
import "./style.scss";
import { escalaBarWidth, escalaRangeLabel, questTipoLabel } from "./dataTransforms";

export default function VerbatinsView() {
  const { C, ESCALA, F, PILLAR_COLORS, getPilarOf } = S;

  return (() => {
    const QUEST = [
      {
        etapa: "NPS Geral",
        tipo: "nps",
        icon: "📊",
        pergunta:
          "Em uma escala de 0 a 10, qual a probabilidade de você recomendar esta Unimed a um amigo ou familiar?",
        escala: "0 a 10",
        top: "9–10 Promotores · 7–8 Neutros · 0–6 Detratores",
        seguimento: "O que mais influenciou sua nota?",
      },
      {
        etapa: "Vendas",
        tipo: "csat",
        icon: "🛒",
        pergunta:
          "Qual foi sua satisfação com o processo de adesão / contratação do plano?",
        escala: "1 a 5",
        top: "4 + 5 = CSAT%",
      },
      {
        etapa: "Boas-Vindas",
        tipo: "csat",
        icon: "🤝",
        pergunta:
          "Qual foi sua satisfação com sua experiência nos primeiros 6 meses com o seu plano de saúde?",
        escala: "1 a 5",
        top: "4 + 5 = CSAT%",
      },
      {
        etapa: "Agend. Consultas",
        tipo: "ces",
        icon: "📅",
        pergunta: "Como foi a facilidade para agendar sua consulta médica?",
        escala: "Ext.Difícil → Ext.Fácil",
        top: "Fácil + Ext.Fácil = CES Top2Box%",
      },
      {
        etapa: "Consulta",
        tipo: "csat",
        icon: "🩺",
        pergunta: "Qual foi sua satisfação com a consulta médica realizada?",
        escala: "1 a 5",
        top: "4 + 5 = CSAT%",
      },
      {
        etapa: "Agend. Exames",
        tipo: "ces",
        icon: "📆",
        pergunta: "Como foi a facilidade para agendar seu exame?",
        escala: "Ext.Difícil → Ext.Fácil",
        top: "Fácil + Ext.Fácil = CES Top2Box%",
      },
      {
        etapa: "Exames",
        tipo: "csat",
        icon: "🔬",
        pergunta: "Qual foi sua satisfação com a realização do exame?",
        escala: "1 a 5",
        top: "4 + 5 = CSAT%",
      },
      {
        etapa: "Atend. Operadora",
        tipo: "ces",
        icon: "🎧",
        pergunta:
          "Como foi a facilidade de resolver sua solicitação ao entrar em contato com a operadora?",
        escala: "Ext.Difícil → Ext.Fácil",
        top: "Fácil + Ext.Fácil = CES Top2Box%",
      },
      {
        etapa: "Aplicativo",
        tipo: "ces",
        icon: "📱",
        pergunta: "Como foi a facilidade de usar o aplicativo da Unimed?",
        escala: "Ext.Difícil → Ext.Fácil",
        top: "Fácil + Ext.Fácil = CES Top2Box%",
      },
      {
        etapa: "Portal",
        tipo: "ces",
        icon: "🌐",
        pergunta: "Como foi a facilidade de usar o portal web da Unimed?",
        escala: "Ext.Difícil → Ext.Fácil",
        top: "Fácil + Ext.Fácil = CES Top2Box%",
      },
      {
        etapa: "Promoção da Saúde",
        tipo: "csat",
        icon: "💊",
        pergunta:
          "Qual foi sua satisfação com os programas de promoção da saúde e prevenção da operadora?",
        escala: "1 a 5",
        top: "4 + 5 = CSAT%",
      },
      {
        etapa: "Comunicação Op.",
        tipo: "csat",
        icon: "💬",
        pergunta:
          "Qual foi sua satisfação com as comunicações e informações enviadas pela operadora?",
        escala: "1 a 5",
        top: "4 + 5 = CSAT%",
      },
    ];
    return (
      <div
        className="tabVerbatinsView"
        style={{ display: "flex", flexDirection: "column", gap: 20 }}
      >
        {/* -- Metodologia -- */}
        <Section title="Metodologia">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              {[
                {
                  icon: "📊",
                  nome: "NPS — Net Promoter Score",
                  cor: C.g700,
                  bg: C.g100,
                  desc: "Indicador âncora de lealdade. Mede a probabilidade de recomendação em escala de 0 a 10. Promotores (9–10), Neutros (7–8) e Detratores (0–6). NPS = %Promotores − %Detratores. Varia de −100 a +100.",
                },
                {
                  icon: "⭐",
                  nome: "CSAT — Customer Satisfaction Score",
                  cor: C.blue,
                  bg: C.blueBg,
                  desc: "Mede a satisfação em etapas específicas da jornada. Escala de 1 a 5: 1 Muito Insatisfeito, 2 Insatisfeito, 3 Razoável, 4 Satisfeito, 5 Muito Satisfeito. Resultado = % de notas 4 e 5 (Top2Box).",
                },
                {
                  icon: "💪",
                  nome: "CES — Customer Effort Score",
                  cor: C.p500,
                  bg: C.p100,
                  desc: "Mede o esforço percebido em etapas operacionais. Escala: Extremamente Difícil, Difícil, Normal, Fácil, Extremamente Fácil. Resultado = % de Fácil + Extremamente Fácil (Top2Box). Quanto maior, melhor.",
                },
                {
                  icon: "🗺️",
                  nome: "Jornada CX — Índice Consolidado",
                  cor: "#d97706",
                  bg: "#FFFBEB",
                  desc: "Média aritmética de todos os indicadores CSAT e CES das 11 etapas da jornada do beneficiário. Representa a experiência geral na jornada de saúde, independente do tipo de indicador.",
                },
              ].map((m, i) => (
                <div
                  key={i}
                  style={{
                    background: m.bg,
                    borderRadius: 12,
                    padding: "14px 16px",
                    border: `1px solid ${m.cor}20`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{m.icon}</span>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: m.cor,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {m.nome}
                    </div>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: C.text2,
                      lineHeight: 1.65,
                    }}
                  >
                    {m.desc}
                  </p>
                </div>
              ))}
            </div>
            {/* Eva qualitative */}
            <div
              style={{
                background: `linear-gradient(135deg,${C.g800},${C.p700})`,
                borderRadius: 14,
                padding: "18px 20px",
              }}
            >
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg,${C.g600},${C.p500})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Sparkles size={20} color="#FFFFFF" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      letterSpacing: "-0.02em",
                      marginBottom: 4,
                    }}
                  >
                    Eva — Entrevistas Qualitativas por IA
                  </div>
                  <p
                    style={{
                      margin: "0 0 10px",
                      fontSize: 12,
                      color: "rgba(255,255,255,.7)",
                      lineHeight: 1.65,
                    }}
                  >
                    Além dos indicadores quantitativos, a plataforma realiza{" "}
                    <b style={{ color: "#FFFFFF" }}>
                      entrevistas de profundidade qualitativas
                    </b>{" "}
                    conduzidas pela Eva, persona sintética gerada por IA que
                    simula o beneficiário com base nos dados coletados.
                  </p>
                  <p
                    style={{
                      margin: "0 0 10px",
                      fontSize: 12,
                      color: "rgba(255,255,255,.7)",
                      lineHeight: 1.65,
                    }}
                  >
                    A Eva é capaz de{" "}
                    <b style={{ color: "#FFFFFF" }}>
                      responder perguntas abertas
                    </b>{" "}
                    como se fosse o beneficiário real — explicando o porquê de
                    cada nota, identificando padrões de sofrimento na jornada e
                    sugerindo melhorias com base em linguagem natural.
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {[
                      "Simula 100% dos respondentes",
                      "Responde em linguagem natural",
                      "Identifica padrões e drivers",
                      "Gera insights proativos",
                      "Disponível 24/7 para consultas",
                    ].map((t) => (
                      <span
                        key={t}
                        style={{
                          background: "rgba(255,255,255,.12)",
                          color: "rgba(255,255,255,.85)",
                          borderRadius: 20,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 500,
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* -- Drivers de NPS — análise qualitativa por IA -- */}
        <Section title="Drivers de NPS — Análise qualitativa por IA">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                padding: "14px 18px",
                background: `linear-gradient(135deg,${C.greenBg},${C.surface})`,
                borderRadius: 12,
                border: `1px solid ${C.g300}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: C.g700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Brain size={18} color="#FFFFFF" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.text,
                      marginBottom: 4,
                    }}
                  >
                    O que são os drivers?
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: C.text2,
                      lineHeight: 1.65,
                    }}
                  >
                    Os drivers representam{" "}
                    <b>os temas que mais aparecem espontaneamente</b> nas
                    respostas abertas e nas entrevistas de profundidade
                    conduzidas pela Eva (IA persona). Cada vez que um
                    beneficiário fala sobre &quot;demorou pra resolver&quot;,
                    &quot;a atendente foi simpática&quot;, &quot;não entendi a
                    cobrança&quot;, esses trechos são{" "}
                    <b>classificados automaticamente</b> em um dos 11 drivers
                    canônicos abaixo, com sentimento positivo, neutro ou
                    negativo.
                  </p>
                </div>
              </div>
              <div
                style={{
                  padding: "10px 14px",
                  background: C.surface,
                  borderRadius: 10,
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                <b style={{ color: C.text }}>
                  Por que isso é diferente de uma pesquisa fechada?
                </b>
                <br />
                Pesquisas tradicionais perguntam &quot;de 1 a 5, como você
                avalia X?&quot;. Aqui, os números vêm das próprias palavras do
                beneficiário — <b>linguagem natural, não checkbox</b>. Isso
                captura nuances que escapam de uma escala (ex: &quot;achei
                rápido mas a atendente foi grossa&quot; vira{" "}
                <i>Agilidade positivo + Humanização negativo</i> no mesmo
                verbatim).
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.text3,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 8,
                }}
              >
                Os 11 drivers canônicos
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 8,
                }}
              >
                {[
                  { label: "Resolutividade no 1º contato", icon: "✅", desc: "Resolveu logo, sem precisar voltar", essence: "sucesso" },
                  { label: "Qualidade médica em consultas", icon: "🩺", desc: "Atendimento médico técnico e atencioso", essence: "sucesso" },
                  { label: "Atend. urgência e emergência", icon: "🚑", desc: "Como foi quando precisou rápido", essence: "sucesso" },
                  { label: "Conciliação (Concordância)", icon: "📋", desc: "Cobertura, autorização, glosa", essence: "sucesso" },
                  { label: "Agilidade no atendimento", icon: "⚡", desc: "Velocidade de resposta e fila de espera", essence: "esforço" },
                  { label: "Dificuldade p/ agendar", icon: "📅", desc: "Conseguir uma data sem batalha", essence: "esforço" },
                  { label: "Reembolso", icon: "💸", desc: "Processo, prazo, transparência", essence: "esforço" },
                  { label: "App facilitador", icon: "📱", desc: "App e portal funcionais e úteis", essence: "esforço" },
                  { label: "Humanização no atendimento", icon: "🤝", desc: "Empatia, escuta, tom", essence: "emoção" },
                  { label: "Proatividade da equipe", icon: "🌱", desc: "Antecipar necessidade, oferecer ajuda", essence: "emoção" },
                  { label: "Comunicação proativa", icon: "💬", desc: "Avisar mudanças, explicar prazos", essence: "emoção" },
                ].map((drv) => {
                  const pilar = getPilarOf(drv.essence) || "Sucesso";
                  const cor = PILLAR_COLORS[pilar];
                  return (
                    <div
                      key={drv.label}
                      style={{
                        padding: "10px 12px",
                        background: C.surf3,
                        borderRadius: 10,
                        border: `1px solid ${C.border}`,
                        borderLeft: `3px solid ${cor}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 6,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            lineHeight: 1.2,
                            flexShrink: 0,
                          }}
                        >
                          {drv.icon}
                        </span>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.text,
                            flex: 1,
                            minWidth: 0,
                            lineHeight: 1.35,
                          }}
                        >
                          {drv.label}
                        </div>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            color: cor,
                            background: `${cor}15`,
                            padding: "2px 6px",
                            borderRadius: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {pilar}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: C.text4,
                          lineHeight: 1.5,
                        }}
                      >
                        {drv.desc}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* -- Drivers emergentes -- */}
            <div
              style={{
                padding: "14px 18px",
                background: `linear-gradient(135deg, ${C.p100}, ${C.surface})`,
                borderRadius: 12,
                border: `1px solid ${C.p500}40`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: C.p700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Sparkles size={18} color="#FFFFFF" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{ fontSize: 13, fontWeight: 700, color: C.text }}
                    >
                      Drivers emergentes
                    </div>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.p700,
                        background: `${C.p700}15`,
                        padding: "2px 7px",
                        borderRadius: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Auto-descoberto
                    </span>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: C.text2,
                      lineHeight: 1.65,
                    }}
                  >
                    Os 11 drivers canônicos acima são o{" "}
                    <b>ponto de partida</b>, não a lista final. À medida que o
                    beneficiário evolui, novos temas começam a aparecer
                    espontaneamente nas pesquisas — sobre{" "}
                    <b>
                      novos serviços, mudanças regulatórias, eventos de mercado
                      ou momentos da operação
                    </b>
                    . A plataforma identifica esses padrões automaticamente:
                    quando um conjunto de citações se repete em volume
                    significativo e não se encaixa em nenhum driver canônico,
                    ele é promovido a <b>driver emergente</b> e passa a ser
                    monitorado.
                  </p>
                </div>
              </div>
              <div
                style={{
                  padding: "10px 14px",
                  background: C.surface,
                  borderRadius: 10,
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                  marginBottom: 10,
                }}
              >
                <b style={{ color: C.text }}>Como um driver emergente nasce?</b>
                <br />
                1. A IA detecta um <b>cluster de citações</b> com vocabulário
                recorrente que não bate com nenhum dos 11 canônicos.
                <br />
                2. O cluster é nomeado automaticamente (ex.:{" "}
                <i>&quot;Telemedicina&quot;</i>,{" "}
                <i>&quot;Coparticipação&quot;</i>,{" "}
                <i>&quot;Atendimento odontológico&quot;</i>) e passa por{" "}
                <b>validação humana</b> antes de virar driver oficial.
                <br />
                3. Após aprovação, o driver entra no painel com sentimento,
                volume e tendência — exatamente como os canônicos.
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[
                  {
                    nome: "Telemedicina",
                    origem: "Detectado em Mar/26 · 142 citações",
                  },
                  {
                    nome: "Coparticipação",
                    origem: "Detectado em Fev/26 · 89 citações",
                  },
                  {
                    nome: "Atend. odontológico",
                    origem: "Detectado em Jan/26 · 67 citações",
                  },
                ].map((d) => (
                  <div
                    key={d.nome}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 10px",
                      background: C.surface,
                      borderRadius: 20,
                      border: `1px dashed ${C.p500}`,
                      fontSize: 10.5,
                    }}
                  >
                    <span style={{ fontSize: 11 }}>✨</span>
                    <span style={{ fontWeight: 600, color: C.text }}>
                      {d.nome}
                    </span>
                    <span style={{ color: C.text4 }}>· {d.origem}</span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 10.5,
                  color: C.text4,
                  fontStyle: "italic",
                  lineHeight: 1.55,
                }}
              >
                Drivers emergentes são revisitados a cada onda de pesquisa. Se
                o volume de citações cair de forma sustentada por dois ciclos
                consecutivos, o driver volta ao status <i>candidato</i> e pode
                ser descontinuado.
              </div>
            </div>
          </div>
        </Section>

        {/* -- Os 3 Pilares de CX -- */}
        <Section title="Os 3 pilares de CX — Sucesso, Esforço e Emoção">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.65 }}>
              Cada um dos 11 drivers se encaixa em um dos{" "}
              <b>3 pilares clássicos da experiência do cliente</b>, framework
              consagrado que organiza a jornada em três perguntas fundamentais
              que o beneficiário faz a si mesmo (consciente ou não) sobre cada
              interação. Agrupar os drivers nesses pilares permite uma{" "}
              <b>leitura executiva rápida</b>: em vez de olhar 11 indicadores, o
              diretor olha 3 e entende em qual dimensão da experiência está o
              problema.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              {[
                {
                  nome: "Sucesso",
                  pergunta: "Você conseguiu o que veio fazer?",
                  desc: "Mede se a Unimed entrega aquilo que prometeu — resolver, autorizar, atender, garantir cobertura. Quando esse pilar cai, é sinal de falha funcional na operação.",
                  cor: PILLAR_COLORS["Sucesso"],
                  drivers: [
                    "Resolutividade no 1º contato",
                    "Qualidade médica em consultas",
                    "Atend. urgência e emergência",
                    "Conciliação (Concordância)",
                  ],
                },
                {
                  nome: "Esforço",
                  pergunta: "Foi difícil ou trabalhoso?",
                  desc: "Mede o quanto o beneficiário precisou batalhar para conseguir algo — fila, repetição, papelada, espera. Quando esse pilar cai, mesmo soluções bem-sucedidas geram atrito e retrabalho de comunicação.",
                  cor: PILLAR_COLORS["Esforço"],
                  drivers: [
                    "Agilidade no atendimento",
                    "Dificuldade p/ agendar",
                    "Reembolso",
                    "App facilitador",
                  ],
                },
                {
                  nome: "Emoção",
                  pergunta: "Como você se sentiu?",
                  desc: "Mede a dimensão humana da experiência — empatia, escuta, antecipação, transparência. É o pilar mais ligado a indicação espontânea (NPS) e ao boca-a-boca positivo na rede.",
                  cor: PILLAR_COLORS["Emoção"],
                  drivers: [
                    "Humanização no atendimento",
                    "Proatividade da equipe",
                    "Comunicação proativa",
                  ],
                },
              ].map((p) => (
                <div
                  key={p.nome}
                  style={{
                    padding: "16px 18px",
                    background: C.surface,
                    borderRadius: 12,
                    border: `1px solid ${C.border}`,
                    borderTop: `4px solid ${p.cor}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: p.cor,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 4,
                    }}
                  >
                    {p.nome}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.text,
                      marginBottom: 8,
                      fontStyle: "italic",
                      lineHeight: 1.3,
                    }}
                  >
                    &ldquo;{p.pergunta}&rdquo;
                  </div>
                  <p
                    style={{
                      margin: "0 0 12px",
                      fontSize: 11,
                      color: C.text3,
                      lineHeight: 1.6,
                    }}
                  >
                    {p.desc}
                  </p>
                  <div
                    style={{
                      paddingTop: 10,
                      borderTop: `1px dashed ${C.border}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.text4,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: 5,
                      }}
                    >
                      {p.drivers.length} drivers neste pilar
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      {p.drivers.map((d) => (
                        <div
                          key={d}
                          style={{
                            fontSize: 10,
                            color: C.text2,
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              background: p.cor,
                            }}
                          />
                          {d}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                padding: "12px 14px",
                background: C.surf3,
                borderRadius: 10,
                fontSize: 11,
                color: C.text3,
                lineHeight: 1.6,
              }}
            >
              <b style={{ color: C.text }}>Como ler na prática:</b> se o pilar{" "}
              <b style={{ color: PILLAR_COLORS["Sucesso"] }}>Sucesso</b> está
              negativo, você tem um problema operacional — não está entregando o
              que promete. Se{" "}
              <b style={{ color: PILLAR_COLORS["Esforço"] }}>Esforço</b> está
              negativo, você até entrega, mas o caminho é doloroso (ótimo lugar
              pra investir em automação e digital). Se{" "}
              <b style={{ color: PILLAR_COLORS["Emoção"] }}>Emoção</b> está
              negativo, falta calor humano e proatividade — investir em
              treinamento, scripts mais empáticos e comunicação preventiva.
            </div>
          </div>
        </Section>

        {/* -- Projeções e tendência -- */}
        <Section title="Projeções e tendência — método de cálculo">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 12, color: C.text2, lineHeight: 1.65 }}>
              Os <b>indicadores de tendência</b> e as{" "}
              <b>projeções 6 / 12 meses</b> mostradas na aba Tendências são
              calculadas via <b>regressão linear por mínimos quadrados</b> sobre
              a série histórica do indicador. O método é determinístico,
              transparente e replicável — não usa LLM, não tem "caixa preta".
            </div>

            <div
              style={{
                padding: "14px 18px",
                background: C.surf3,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                  marginBottom: 10,
                }}
              >
                1. Cálculo da reta de tendência
              </div>
              <p
                style={{
                  margin: "0 0 10px",
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                Pra cada série de N pontos{" "}
                <code
                  style={{
                    background: C.surface,
                    padding: "1px 5px",
                    borderRadius: 4,
                    fontFamily: F.mono,
                    fontSize: 10,
                  }}
                >{`(x₁,y₁), (x₂,y₂), ..., (xₙ,yₙ)`}</code>
                , calculamos os parâmetros <b>m</b> (inclinação) e <b>b</b>{" "}
                (intercepto) da reta{" "}
                <code
                  style={{
                    background: C.surface,
                    padding: "1px 5px",
                    borderRadius: 4,
                    fontFamily: F.mono,
                    fontSize: 10,
                  }}
                >{`y = m·x + b`}</code>{" "}
                que minimizam a soma dos quadrados dos resíduos:
              </p>
              <div
                style={{
                  padding: "10px 14px",
                  background: C.surface,
                  borderRadius: 8,
                  fontFamily: F.mono,
                  fontSize: 11,
                  color: C.text2,
                  lineHeight: 1.7,
                  border: `1px solid ${C.border}`,
                }}
              >
                m = Σ((xᵢ − x̄)·(yᵢ − ȳ)) / Σ((xᵢ − x̄)²)
                <br />b = ȳ − m·x̄
              </div>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                Onde <b>x̄</b> é a média dos períodos e <b>ȳ</b> é a média dos
                valores observados. O resultado é a reta que melhor descreve o
                ritmo de variação da série.
              </p>
            </div>

            <div
              style={{
                padding: "14px 18px",
                background: C.surf3,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                  marginBottom: 10,
                }}
              >
                2. Classificação da tendência
              </div>
              <p
                style={{
                  margin: "0 0 10px",
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                A inclinação <b>m</b> determina o rótulo visual exibido nos
                cards:
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                  gap: 8,
                }}
              >
                {[
                  {
                    label: "Tendência de alta",
                    icon: "↗",
                    desc: "m ≥ +0,3",
                    cor: C.green,
                  },
                  {
                    label: "Tendência de estabilidade",
                    icon: "→",
                    desc: "|m| &lt; 0,3",
                    cor: C.orange,
                  },
                  {
                    label: "Tendência de baixa",
                    icon: "↘",
                    desc: "m ≤ −0,3",
                    cor: C.red,
                  },
                ].map((t) => (
                  <div
                    key={t.label}
                    style={{
                      padding: "10px 12px",
                      background: C.surface,
                      borderRadius: 8,
                      border: `1px solid ${t.cor}30`,
                      borderLeft: `3px solid ${t.cor}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ fontSize: 14, color: t.cor }}>
                        {t.icon}
                      </span>
                      <div
                        style={{ fontSize: 11, fontWeight: 700, color: t.cor }}
                      >
                        {t.label}
                      </div>
                    </div>
                    <code
                      style={{
                        fontSize: 10,
                        color: C.text3,
                        fontFamily: F.mono,
                      }}
                      dangerouslySetInnerHTML={{ __html: t.desc }}
                    />
                  </div>
                ))}
              </div>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 10,
                  color: C.text4,
                  lineHeight: 1.6,
                  fontStyle: "italic",
                }}
              >
                O limiar de 0,3 unidade/período foi calibrado pra evitar
                classificar ruído estatístico como tendência real. Pode ser
                ajustado por cliente quando o histórico tiver volume suficiente
                pra avaliar sensibilidade.
              </p>
            </div>

            <div
              style={{
                padding: "14px 18px",
                background: C.surf3,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                  marginBottom: 10,
                }}
              >
                3. Projeção 6 e 12 meses (com range)
              </div>
              <p
                style={{
                  margin: "0 0 10px",
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                Para projetar o valor em <b>k</b> períodos adiante, aplicamos a
                reta no ponto futuro:
              </p>
              <div
                style={{
                  padding: "10px 14px",
                  background: C.surface,
                  borderRadius: 8,
                  fontFamily: F.mono,
                  fontSize: 11,
                  color: C.text2,
                  lineHeight: 1.7,
                  border: `1px solid ${C.border}`,
                }}
              >
                ŷ(N+k) = m·(N+k) + b
              </div>
              <p
                style={{
                  margin: "10px 0",
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                Como nenhuma projeção é exata, calculamos um{" "}
                <b>range de incerteza</b> baseado no{" "}
                <b>erro padrão dos resíduos</b>:
              </p>
              <div
                style={{
                  padding: "10px 14px",
                  background: C.surface,
                  borderRadius: 8,
                  fontFamily: F.mono,
                  fontSize: 11,
                  color: C.text2,
                  lineHeight: 1.7,
                  border: `1px solid ${C.border}`,
                }}
              >
                SE = √(Σ(yᵢ − ŷᵢ)² / (N−2))
                <br />
                range = ŷ ± 1,5 · SE
              </div>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 11,
                  color: C.text3,
                  lineHeight: 1.65,
                }}
              >
                Esse range corresponde a um intervalo de aproximadamente{" "}
                <b>80% de confiança</b> assumindo que os resíduos são
                normalmente distribuídos. Quanto mais oscilante a série
                histórica, maior o erro padrão e portanto maior o range. Séries
                muito estáveis geram ranges estreitos.
              </p>
            </div>

            <div
              style={{
                padding: "12px 14px",
                background: `${C.orange}10`,
                borderRadius: 10,
                border: `1px solid ${C.orange}30`,
                fontSize: 11,
                color: C.text3,
                lineHeight: 1.6,
              }}
            >
              <b style={{ color: C.text }}>⚠ Limitações importantes:</b> a
              regressão linear assume que{" "}
              <b>o ritmo dos últimos meses se mantém</b>. Não captura
              sazonalidade, eventos pontuais (campanhas, crises, mudanças
              regulatórias) nem o impacto de planos de ação ainda não
              executados. É uma <b>extrapolação cega</b> útil pra alertar quando
              algo está fora do trilho, mas{" "}
              <b>não substitui análise qualitativa</b>. Para horizontes maiores
              que 12 meses, modelos mais sofisticados (ARIMA, Prophet, redes
              neurais) seriam necessários — mas a ganho de precisão seria
              pequeno frente à própria volatilidade do CX.
            </div>
          </div>
        </Section>

        {/* -- Escala de referência — NPS -- */}
        <Section title="Escala de referência — NPS">
          <div style={{ fontSize: 11, color: C.text4, marginBottom: 10 }}>
            O NPS varia de −100 a +100 e segue uma escala própria com 4 zonas de
            classificação. Cada zona representa um nível de saúde relativa da
            base de clientes em termos de lealdade e propensão a recomendação.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              {
                label: "✦ Excelência",
                faixa: "75 a 100",
                desc: "Base muito leal, com forte concentração de promotores. Diferencial competitivo claro. Foco: amplificar e defender.",
                color: "#059669",
                bg: "#ECFDF5",
                barW: "100%",
              },
              {
                label: "✓ Qualidade",
                faixa: "50 a 74",
                desc: "Base saudável, mais promotores do que detratores. Margem para crescer. Foco: identificar drivers de promoção e replicar.",
                color: "#16a34a",
                bg: "#F0FDF4",
                barW: "75%",
              },
              {
                label: "→ Aperfeiçoamento",
                faixa: "0 a 49",
                desc: "Equilíbrio frágil. Promotores e detratores próximos. Foco: atacar drivers de atrito antes que virem churn.",
                color: "#d97706",
                bg: "#FFFBEB",
                barW: "50%",
              },
              {
                label: "✗ Crítico",
                faixa: "−100 a −1",
                desc: "Mais detratores do que promotores. Risco real de evasão e dano reputacional. Foco: intervenção urgente em causas raiz.",
                color: "#991b1b",
                bg: "#FFF1F2",
                barW: "25%",
              },
            ].map((z, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  background: z.bg,
                  borderRadius: 10,
                  border: `1px solid ${z.color}25`,
                }}
              >
                <div
                  style={{
                    width: 60,
                    height: 36,
                    borderRadius: 10,
                    background: z.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: "#fff",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {z.faixa}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: z.color,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {z.label}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.text3,
                      lineHeight: 1.5,
                      marginTop: 2,
                    }}
                  >
                    {z.desc}
                  </div>
                </div>
                <div
                  style={{
                    height: 8,
                    width: 120,
                    background: "rgba(0,0,0,.06)",
                    borderRadius: 4,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: z.color,
                      borderRadius: 4,
                      width: z.barW,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              background: C.g100,
              borderRadius: 10,
              marginTop: 10,
              border: `1px solid ${C.g700}20`,
            }}
          >
            <Lightbulb size={16} color={C.g700} style={{ flexShrink: 0 }} />
            <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.5 }}>
              <b style={{ color: C.g700 }}>Cálculo:</b> NPS = % Promotores − %
              Detratores. Promotores são quem deu nota 9 ou 10. Detratores são
              quem deu nota de 0 a 6. Neutros (notas 7 e 8) <b>não entram</b> no
              cálculo, mas representam uma base que pode se mover para qualquer
              lado.
            </div>
          </div>
        </Section>

        {/* -- Escala de referência — CSAT e CES -- */}
        <Section title="Escala de referência — CSAT e CES">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ESCALA.map((e, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  background: e.bg,
                  borderRadius: 10,
                  border: `1px solid ${e.color}25`,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: e.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{ fontSize: 14, color: "#fff", fontWeight: 700 }}
                  >
                    {e.min === 80 ? "≥80" : e.min === 0 ? "<40" : e.min + "%"}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: e.color,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {e.label}
                  </div>
                  <div style={{ fontSize: 11, color: C.text4 }}>
                    {escalaRangeLabel(ESCALA, i)}
                  </div>
                </div>
                <div
                  style={{
                    height: 8,
                    width: 120,
                    background: "rgba(0,0,0,.06)",
                    borderRadius: 4,
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: e.color,
                      borderRadius: 4,
                      width: escalaBarWidth(i),
                    }}
                  />
                </div>
              </div>
            ))}
            <div
              style={{
                fontSize: 11,
                color: C.text4,
                marginTop: 4,
                padding: "0 4px",
              }}
            >
              * Esta escala se aplica igualmente a indicadores CES (Top2Box%) e
              CSAT (% notas 4+5).
            </div>
          </div>
        </Section>

        {/* -- Questionário completo -- */}
        <Section title="Questionário completo — 11 etapas + NPS">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {QUEST.map((q, qi) => {
              const isCes = q.tipo === "ces",
                isNps = q.tipo === "nps";
              const cor = isNps ? C.g700 : isCes ? C.blue : C.g600;
              const bgCor = isNps ? C.g100 : isCes ? C.blueBg : C.greenBg;
              const tipLabel = questTipoLabel(q.tipo);
              return (
                <div
                  key={qi}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    padding: "12px 14px",
                    background: C.surf3,
                    borderRadius: 12,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      background: bgCor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    <span style={{ fontSize: 15 }}>{q.icon}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        alignItems: "center",
                        marginBottom: 5,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {qi + 1}. {q.etapa}
                      </span>
                      <Tag label={tipLabel} color={cor} bg={bgCor} />
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: C.text,
                        fontStyle: "italic",
                        marginBottom: 6,
                        lineHeight: 1.5,
                      }}
                    >
                      "{q.pergunta}"
                    </div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 10,
                          color: C.text4,
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <span style={{ fontWeight: 600, color: C.text3 }}>
                          Escala:
                        </span>{" "}
                        {q.escala}
                      </span>
                      <span
                        style={{ fontSize: 10, color: cor, fontWeight: 600 }}
                      >
                        {q.top}
                      </span>
                      {q.seguimento && (
                        <span
                          style={{
                            fontSize: 10,
                            color: C.text4,
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <span style={{ fontWeight: 600, color: C.text3 }}>
                            Follow-up:
                          </span>{" "}
                          {q.seguimento}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* -- LGPD Compliance e Código de Ética em Pesquisa -- */}
        <Section title="LGPD Compliance e Código de Ética em Pesquisa">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Card LGPD */}
            <div
              style={{
                background: `linear-gradient(135deg,#F0FDF4,${C.surface})`,
                border: `1px solid ${C.g300}`,
                borderRadius: 14,
                padding: "18px 22px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: C.g700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: 20 }}>🛡️</span>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.g800 || C.text,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Lei Geral de Proteção de Dados (Lei 13.709/2018)
                  </div>
                  <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                    Compromisso da Hazo.ai com a privacidade e proteção dos
                    dados dos beneficiários
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: C.text2,
                  lineHeight: 1.7,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <p style={{ margin: 0 }}>
                  A plataforma <b>121 Insights</b> opera em total conformidade
                  com a Lei Geral de Proteção de Dados (LGPD). Todas as
                  pesquisas conduzidas utilizam{" "}
                  <b>consentimento explícito e informado</b> do beneficiário,
                  coletado no momento do convite à participação, com descrição
                  clara da finalidade, dos dados coletados e da forma de
                  armazenamento.
                </p>
                <p style={{ margin: 0 }}>
                  Os dados pessoais dos beneficiários são{" "}
                  <b>anonimizados por padrão</b> em todas as visualizações
                  agregadas. Nenhum indicador no dashboard revela dados
                  individuais identificáveis.
                </p>
                <p style={{ margin: 0 }}>
                  A operadora contratante (Unimed) atua como{" "}
                  <b>controladora dos dados</b>, e a 121Labs como{" "}
                  <b>operadora</b>, conforme definido nos artigos 5º, VI e VII
                  da LGPD. Todos os papéis, responsabilidades, prazos de
                  retenção e protocolos de incidente estão formalizados em
                  contrato via DPA (Data Processing Agreement) específico por
                  cliente.
                </p>
              </div>
            </div>

            {/* Direitos do titular */}
            <div
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                padding: "16px 20px",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                  marginBottom: 10,
                  letterSpacing: "-0.01em",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 15 }}>⚖️</span> Direitos do titular dos
                dados (Art. 18 da LGPD)
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                {[
                  {
                    t: "Confirmação e acesso",
                    d: "Saber se seus dados estão sendo tratados e ter acesso a eles a qualquer momento.",
                  },
                  {
                    t: "Correção",
                    d: "Solicitar correção de dados incompletos, inexatos ou desatualizados.",
                  },
                  {
                    t: "Anonimização ou eliminação",
                    d: "Pedir a anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade.",
                  },
                  {
                    t: "Portabilidade",
                    d: "Receber os dados em formato estruturado, interoperável e legível por máquina.",
                  },
                  {
                    t: "Revogação de consentimento",
                    d: "Revogar o consentimento a qualquer momento, sem prejuízo à continuidade de seus serviços de saúde.",
                  },
                  {
                    t: "Informação sobre compartilhamento",
                    d: "Saber com quais entidades públicas ou privadas seus dados foram compartilhados.",
                  },
                ].map((d, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "9px 12px",
                      background: C.surf3,
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.text,
                        marginBottom: 2,
                      }}
                    >
                      {d.t}
                    </div>
                    <div
                      style={{ fontSize: 10, color: C.text3, lineHeight: 1.5 }}
                    >
                      {d.d}
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 14px",
                  background: C.g100 || C.greenBg,
                  borderRadius: 10,
                  fontSize: 11,
                  color: C.text2,
                  lineHeight: 1.5,
                  border: `1px solid ${C.g300}`,
                }}
              >
                <b>Canal de atendimento ao titular:</b> Os dados de pesquisa
                que chegam já estão anonimizados e, por isso, não constituem
                dado pessoal nos termos do Art. 5º, XI da LGPD. Ainda assim, o
                beneficiário pode exercer os direitos previstos no Art. 18
                sobre os dados pessoais que a operadora detém antes da
                anonimização (cadastro, contato, vínculo, histórico) contatando
                o DPO (Encarregado de Proteção de Dados) designado pela
                operadora, cujo e-mail é divulgado no site institucional e no
                portal do beneficiário. A 121Labs apoia a operadora respondendo
                em até <b>15 dias úteis</b> quaisquer solicitações encaminhadas
                pelo DPO que envolvam etapas anteriores à anonimização.
              </div>
            </div>

            {/* Código de Ética em Pesquisa */}
            <div
              style={{
                background: `linear-gradient(135deg,${C.blueBg},${C.surface})`,
                border: `1px solid ${C.blue}30`,
                borderRadius: 14,
                padding: "18px 22px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: C.blue,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: 20 }}>📜</span>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.blue,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Código de Ética em Pesquisa de Mercado e Opinião
                  </div>
                  <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                    Baseado no Código ESOMAR/ICC e adaptado ao contexto
                    brasileiro de saúde
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: C.text2,
                  lineHeight: 1.7,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <p style={{ margin: 0 }}>
                  A condução de pesquisas com beneficiários de planos de saúde
                  exige um nível adicional de cuidado ético, dada a
                  sensibilidade dos dados e o contexto de vulnerabilidade
                  eventual em que os respondentes podem estar. A Hazo.ai adota
                  os princípios do{" "}
                  <b>
                    Código Internacional ICC/ESOMAR para Pesquisa de Mercado,
                    Pesquisa Social e de Opinião e Análise de Dados
                  </b>
                  , com as seguintes práticas vinculantes:
                </p>
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {[
                  {
                    n: "1",
                    t: "Participação voluntária e não-coercitiva",
                    d: "Nenhum beneficiário é obrigado a responder pesquisas. A recusa não afeta o acesso a serviços de saúde, prazos de autorização ou qualquer benefício contratual.",
                  },
                  {
                    n: "2",
                    t: "Transparência de finalidade",
                    d: "Todo convite à pesquisa declara claramente quem está pesquisando, o propósito da pesquisa, o tempo estimado de resposta, e como os dados serão utilizados.",
                  },
                  {
                    n: "3",
                    t: "Proteção de grupos vulneráveis",
                    d: "Pesquisas não são aplicadas a beneficiários em tratamento ativo de condições graves sem validação prévia da área médica da operadora. Menores de 18 anos exigem consentimento do responsável legal.",
                  },
                  {
                    n: "4",
                    t: "Separação entre pesquisa e venda",
                    d: "Os dados coletados em pesquisas de NPS/CSAT/CES nunca são utilizados para abordagem comercial, upsell, cobrança ou qualquer finalidade estranha à melhoria do serviço. Esta é uma regra inviolável da plataforma.",
                  },
                  {
                    n: "5",
                    t: "Confidencialidade do respondente",
                    d: "Respostas individuais jamais são compartilhadas com a equipe assistencial ou comercial de forma identificável. Apenas agregados estatísticos (n ≥ 30) são disponibilizados no dashboard.",
                  },
                  {
                    n: "6",
                    t: "Direito ao silêncio e à revisão",
                    d: "O beneficiário pode solicitar a exclusão de qualquer resposta já fornecida a qualquer momento, sem necessidade de justificativa.",
                  },
                  {
                    n: "7",
                    t: "Fidelidade ao dado bruto",
                    d: "Nenhum resultado é manipulado, filtrado ou interpretado para favorecer a operadora. Alertas negativos são tão visíveis quanto elogios. A IA Eva opera sobre dados brutos sem filtros comerciais.",
                  },
                  {
                    n: "8",
                    t: "Auditoria independente",
                    d: "A metodologia é validada anualmente por auditoria independente conduzida pela 121Labs, com relatório resumido disponível à operadora contratante.",
                  },
                ].map((item) => (
                  <div
                    key={item.n}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      padding: "10px 12px",
                      background: C.surface,
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: C.blue,
                        color: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      {item.n}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.text,
                          marginBottom: 2,
                        }}
                      >
                        {item.t}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: C.text3,
                          lineHeight: 1.5,
                        }}
                      >
                        {item.d}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Infraestrutura técnica de segurança */}
            <div
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 14,
                padding: "16px 20px",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                  marginBottom: 10,
                  letterSpacing: "-0.01em",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 15 }}>🔐</span> Infraestrutura de
                segurança da informação
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                  gap: 8,
                }}
              >
                {[
                  {
                    i: "🔒",
                    t: "Criptografia em repouso e em trânsito",
                    d: "TLS 1.3 para todo tráfego, AES-256 para dados armazenados.",
                  },
                  {
                    i: "🏛️",
                    t: "Armazenamento em território nacional",
                    d: "Servidores hospedados em datacenters brasileiros para conformidade com Art. 33 da LGPD.",
                  },
                  {
                    i: "📋",
                    t: "Trilha de auditoria completa",
                    d: "Cada acesso, alteração e consulta é registrado com usuário, timestamp e IP de origem.",
                  },
                  {
                    i: "🗑️",
                    t: "Retenção configurável",
                    d: "Prazos de retenção por tipo de dado definidos em DPA e aplicados automaticamente pelo sistema.",
                  },
                  {
                    i: "🚨",
                    t: "Protocolo de incidentes",
                    d: "Em caso de incidente de segurança, notificação à ANPD e aos titulares em até 48 horas.",
                  },
                  {
                    i: "✅",
                    t: "Avaliação de impacto (DPIA)",
                    d: "Análise de Impacto à Proteção de Dados Pessoais que mapeia o fluxo de dados da plataforma, analisa os riscos envolvidos e define medidas de mitigação para proteger a privacidade dos beneficiários.",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "10px 12px",
                      background: C.surf3,
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ fontSize: 14 }}>{item.i}</span>
                      <div
                        style={{ fontSize: 11, fontWeight: 700, color: C.text }}
                      >
                        {item.t}
                      </div>
                    </div>
                    <div
                      style={{ fontSize: 10, color: C.text3, lineHeight: 1.45 }}
                    >
                      {item.d}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>
    );
  })();
}
